/* ----------------------------------------------------------------------------
 * WordPress DRAFT-push — data (fetch) layer. Thin, typed, workspace-scoped.
 *
 * The single authorised call here is POST /v1/connector/publications, which
 * pushes an APPROVED (status 'ready') blog draft to a connected site as a
 * WordPress DRAFT. Workspace scope + the human session/CSRF gate are enforced
 * SERVER-SIDE (the request rides the cookie session; lib/api adds the CSRF
 * double-submit + idempotency headers on the POST). The caller never supplies
 * tenancy.
 *
 * DRAFT-ONLY: the request body carries ONLY draft_id + target_id (+ optional
 * push_options) — there is no status/publish field to send, and the server
 * hard-codes the WordPress status to 'draft'. isDraftOnlyResult defends in depth
 * so a non-draft server response can never be surfaced as a success.
 * -------------------------------------------------------------------------- */

import { api } from '@/lib/api';
import { CONNECTOR_ENDPOINTS } from './connections-model';
import {
  buildPublicationBody,
  isDraftOnlyResult,
  type PushResult,
} from '@/voice/actions/contentActionModel';

/** POST /v1/connector/publications response (ConnectorRoutes::push). */
interface PublicationDto {
  ok?: boolean;
  draft_id?: number;
  target_id?: number;
  remote_post_id?: string | number | null;
  remote_status?: string;
  remote_edit_url?: string | null;
  publish_mode?: string; // always 'draft_only'
}

/**
 * Push an APPROVED blog draft to a connected site as a WordPress DRAFT.
 * Resolves to a PushResult; a non-draft server outcome is downgraded to
 * `ok:false` (defence in depth — the UI never claims a publish happened).
 */
export async function pushBlogDraftToWordPress(
  draftId: number,
  targetId: number,
  pushOptions: Record<string, unknown> = {},
): Promise<PushResult> {
  const res = await api.post<PublicationDto>(
    CONNECTOR_ENDPOINTS.publications,
    buildPublicationBody(draftId, targetId, pushOptions),
  );
  const remoteStatus = typeof res.remote_status === 'string' ? res.remote_status : '';
  const remotePostId = res.remote_post_id != null ? String(res.remote_post_id) : null;
  const editUrl =
    typeof res.remote_edit_url === 'string' && res.remote_edit_url !== ''
      ? res.remote_edit_url
      : undefined;
  return {
    // Success ONLY when the server said ok AND the outcome is draft-family.
    ok: res.ok === true && isDraftOnlyResult(remoteStatus),
    remotePostId,
    remoteStatus,
    editUrl,
  };
}
